import React, { useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCorners, MeasuringStrategy,
} from '@dnd-kit/core';
import { JobColumn } from './JobColumn';
import { JobCard } from './JobCard';
import { STAGES, T, stageMeta } from '../constants';

/**
 * Horizontally scrolling Kanban board.
 *
 * Desktop/tablet: every stage side by side, scrolling sideways when needed.
 * Mobile: a stage picker showing one column at a time — squeezing seven
 * columns onto a phone would make the cards unreadable.
 */
/**
 * The board scrolls sideways, and it keeps scrolling while a card is being
 * dragged (dnd-kit auto-scrolls near the edges). dnd-kit's cached droppable
 * rects are captured before that scrolling, so by drop time they describe where
 * the columns *were*: dropping on one column would file the card under another,
 * or silently do nothing. `MeasuringStrategy.Always` does not help — the cached
 * rects still lag a mid-drag scroll.
 *
 * Measuring the columns straight from the DOM at collision time sidesteps the
 * cache entirely, so the drop always matches the column under the pointer. Eight
 * getBoundingClientRect calls per pointer move is negligible.
 */
const boardCollisionDetection = (args) => {
  const { droppableContainers, pointerCoordinates } = args;

  if (pointerCoordinates) {
    const hits = [];
    for (const container of droppableContainers) {
      const node = container?.node?.current;
      if (!node) continue;
      const rect = node.getBoundingClientRect();
      const inside = pointerCoordinates.x >= rect.left && pointerCoordinates.x <= rect.right
        && pointerCoordinates.y >= rect.top && pointerCoordinates.y <= rect.bottom;
      if (inside) hits.push({ id: container.id, data: { droppableContainer: container } });
    }
    if (hits.length) return hits;
  }

  // Pointer outside every column (dragged above or below the board).
  return closestCorners(args);
};

// Keep dnd-kit's own measurements fresh for the overlay and auto-scroll maths.
const measuring = { droppable: { strategy: MeasuringStrategy.Always } };

/**
 * Auto-scroll is off.
 *
 * Hovering near either edge of the board made it scroll, and because it keeps
 * scrolling for as long as you hover there it would race to the far end. The
 * columns slid out from under the pointer mid-drag, so releasing over (say)
 * Bookmarked filed the card under Rejected instead. Throttling the threshold
 * and acceleration only slowed the runaway down; it did not stop it.
 *
 * Without it, a drop always lands on the column the pointer is actually over.
 * Reaching an off-screen stage means scrolling the board first, or using the
 * stage buttons in a card's details — both of which already work.
 */
const autoScroll = false;

export const JobBoard = ({ byStage, onOpenJob, onAddToStage, onMoveJob }) => {
  const [activeJob, setActiveJob] = useState(null);
  const [mobileStage, setMobileStage] = useState(STAGES[0].id);

  // A small activation distance keeps plain clicks working as clicks.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = ({ active }) => {
    const stage = active.data.current?.stage;
    setActiveJob((byStage[stage] || []).find(j => j._id === active.id) || null);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveJob(null);
    if (!over) return;
    // Dropped on a column, or on a card already inside one.
    const targetStage = STAGES.some(s => s.id === over.id)
      ? over.id
      : over.data.current?.stage;
    if (!targetStage) return;
    if (active.data.current?.stage === targetStage) return;
    onMoveJob(active.id, targetStage);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollisionDetection}
      measuring={measuring}
      autoScroll={autoScroll}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveJob(null)}
    >
      {/* Mobile stage picker */}
      <div className="jt-stage-picker" style={{ display: 'none', gap: 6, overflowX: 'auto', paddingBottom: 10, marginBottom: 4 }}>
        {STAGES.map((stage) => {
          const active = stage.id === mobileStage;
          const count = (byStage[stage.id] || []).length;
          const Icon = stage.icon;
          return (
            <button
              key={stage.id}
              type="button"
              onClick={() => setMobileStage(stage.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                background: active ? T.brandSoft : T.surface,
                border: `1px solid ${active ? T.brandBorder : T.line}`,
                color: active ? T.brandDark : T.muted,
                borderRadius: 20, padding: '7px 13px',
                fontSize: 12.5, fontWeight: active ? 700 : 600,
                cursor: 'pointer', fontFamily: T.font, whiteSpace: 'nowrap',
              }}
            >
              <Icon size={13} strokeWidth={2.1} color={active ? T.brand : stage.color} />
              {stage.label}
              <span style={{
                fontSize: 10.5, fontWeight: 800,
                background: active ? T.brand : stage.bg,
                color: active ? 'white' : stage.color,
                borderRadius: 10, padding: '0 6px', minWidth: 18, textAlign: 'center',
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Board */}
      <div className="jt-board" role="list">
        {STAGES.map(stage => (
          <div
            key={stage.id}
            role="listitem"
            className={`jt-board-col${stage.id === mobileStage ? ' jt-board-col-active' : ''}`}
          >
            <JobColumn
              stageId={stage.id}
              jobs={byStage[stage.id] || []}
              onOpenJob={onOpenJob}
              onAddToStage={onAddToStage}
            />
          </div>
        ))}
      </div>

      {/* Floating preview of the card being dragged */}
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(.18,.67,.6,1.22)' }}>
        {activeJob ? (
          <div style={{ width: 248, transform: 'rotate(1.5deg)', cursor: 'grabbing' }}>
            <div style={{
              borderRadius: T.radius,
              boxShadow: '0 18px 40px rgba(15,23,42,0.22)',
              border: `1px solid ${stageMeta(activeJob.stage).color}55`,
            }}>
              <JobCard job={activeJob} onOpen={() => {}} />
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
